import * as React from "react";

import type { DragEndEvent } from "@dnd-kit/core";

import {
  DndContext,
  getClientRect,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "../../utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    className={cn(
      "af-fixed af-inset-0 af-z-50 af-bg-foreground/30 data-[state=open]:af-animate-in data-[state=closed]:af-animate-out data-[state=closed]:af-fade-out-0 data-[state=open]:af-fade-in-0",
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = "DialogOverlay";

interface DialogContentProps extends React.ComponentProps<
  typeof DialogPrimitive.Content
> {
  overlay?: React.ReactNode;
}

const DialogContent = ({
  className,
  children,
  overlay = <DialogOverlay />,
  ...props
}: DialogContentProps) => (
  <DialogPortal>
    {overlay}
    <DialogPrimitive.Content
      className={cn(
        "af-fixed af-left-[50%] af-top-[50%] af-z-50 af-grid af-w-full af-max-w-lg af-translate-x-[-50%] af-translate-y-[-50%] af-gap-4 af-border af-bg-background af-p-6 af-shadow-lg af-duration-200 data-[state=open]:af-animate-in data-[state=closed]:af-animate-out data-[state=closed]:af-fade-out-0 data-[state=open]:af-fade-in-0 data-[state=closed]:af-zoom-out-95 data-[state=open]:af-zoom-in-95 data-[state=closed]:af-slide-out-to-left-1/2 data-[state=closed]:af-slide-out-to-top-[48%] data-[state=open]:af-slide-in-from-left-1/2 data-[state=open]:af-slide-in-from-top-[48%] sm:af-rounded-lg",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

interface DraggableDialogContentProps extends DialogContentProps {
  dragHandleLabel?: string;
}

const DIALOG_KEYBOARD_MOVE_DISTANCE = 10;

const DraggableDialogContent = ({
  className,
  children,
  dragHandleLabel = "Move dialog",
  overlay = null,
  ...props
}: DraggableDialogContentProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
  );
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  const handleDragEnd = ({ delta }: DragEndEvent) => {
    setPosition(({ x, y }) => ({ x: x + delta.x, y: y + delta.y }));
  };

  const handleKeyboardMove = (delta: { x: number; y: number }) => {
    setPosition(({ x, y }) => ({ x: x + delta.x, y: y + delta.y }));
  };

  return (
    <DialogPortal>
      {overlay}
      <DndContext
        measuring={{ draggable: { measure: getClientRect } }}
        modifiers={[restrictToWindowEdges]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <DraggableDialogContentInner
          className={className}
          dragHandleLabel={dragHandleLabel}
          onKeyboardMove={handleKeyboardMove}
          position={position}
          {...props}
        >
          {children}
        </DraggableDialogContentInner>
      </DndContext>
    </DialogPortal>
  );
};
DraggableDialogContent.displayName = "DraggableDialogContent";

interface DraggableDialogContentInnerProps extends DialogContentProps {
  dragHandleLabel: string;
  onKeyboardMove: (delta: { x: number; y: number }) => void;
  position: { x: number; y: number };
}

const DraggableDialogContentInner = ({
  className,
  children,
  dragHandleLabel,
  onKeyboardMove,
  position,
  ...props
}: DraggableDialogContentInnerProps) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: "dialog",
  });

  const x = position.x + (transform?.x ?? 0);
  const y = position.y + (transform?.y ?? 0);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    if (
      target.closest(
        "button, a, input, textarea, select, [contenteditable=true], [data-dialog-drag-cancel]",
      )
    ) {
      return;
    }

    listeners?.onPointerDown?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    const keyboardDeltas: Partial<Record<string, { x: number; y: number }>> = {
      ArrowDown: { x: 0, y: DIALOG_KEYBOARD_MOVE_DISTANCE },
      ArrowLeft: { x: -DIALOG_KEYBOARD_MOVE_DISTANCE, y: 0 },
      ArrowRight: { x: DIALOG_KEYBOARD_MOVE_DISTANCE, y: 0 },
      ArrowUp: { x: 0, y: -DIALOG_KEYBOARD_MOVE_DISTANCE },
    };
    const delta = keyboardDeltas[event.key];

    if (!delta) {
      return;
    }

    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const viewport = event.currentTarget.ownerDocument.documentElement;
    onKeyboardMove({
      x: Math.min(
        Math.max(delta.x, -rect.left),
        viewport.clientWidth - rect.right,
      ),
      y: Math.min(
        Math.max(delta.y, -rect.top),
        viewport.clientHeight - rect.bottom,
      ),
    });
  };

  return (
    <DialogPrimitive.Content
      ref={setNodeRef}
      aria-description={dragHandleLabel}
      className={cn(
        "af-fixed af-left-[50%] af-top-[50%] af-z-50 af-grid af-w-full af-max-w-lg af-cursor-move af-touch-none af-gap-4 af-border af-bg-background af-p-6 af-shadow-lg active:af-cursor-grabbing sm:af-rounded-lg",
        className,
      )}
      style={{
        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
      }}
      {...props}
      {...attributes}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      role="dialog"
    >
      {children}
    </DialogPrimitive.Content>
  );
};
DraggableDialogContentInner.displayName = "DraggableDialogContentInner";

const DialogCloseButton = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) => (
  <DialogPrimitive.Close
    className={cn(
      "af-absolute af-right-4 af-top-4 af-rounded-sm af-opacity-70 af-ring-offset-background af-transition-opacity hover:af-opacity-100 focus:af-outline-none focus:af-ring-2 focus:af-ring-ring focus:af-ring-offset-2 disabled:af-pointer-events-none data-[state=open]:af-bg-accent data-[state=open]:af-text-muted-foreground",
      className,
    )}
    {...props}
  >
    <X className="af-h-4 af-w-4 af-text-foreground" />
    <span className="af-sr-only">Close</span>
  </DialogPrimitive.Close>
);
DialogCloseButton.displayName = "DialogCloseButton";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "af-flex af-flex-col af-space-y-1.5 af-text-center sm:af-text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "af-flex af-flex-col-reverse sm:af-flex-row sm:af-justify-end sm:af-space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    className={cn("af-text-lg af-font-semibold af-leading-none", className)}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    className={cn("af-text-sm af-text-muted-foreground", className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DraggableDialogContent,
};
